import { createElement, lazy, type ComponentType, type ReactElement } from 'react';
import { SITE_PATHS } from '../../../shared/siteRoutes';
import { HELP_ARTICLES, HELP_INDEX_PATH, helpPath } from '../../../shared/helpArticles';

/**
 * The public site's page components, one per registry path (shared/siteRoutes.ts). Each is code-split (React.lazy)
 * until `preload()` has loaded it; after that it renders synchronously. The server renderer preloads them all before
 * rendering (renderToString does not wait for Suspense), and the browser preloads the current page before hydrating
 * a prerendered document, so both render the same tree.
 */
export type SitePage = (() => ReactElement) & { preload(): Promise<void> };

function sitePage(load: () => Promise<{ default: ComponentType }>): SitePage {
  let loaded: ComponentType | null = null;
  const Lazy = lazy(load);
  const Page = (() => createElement(loaded ?? Lazy)) as SitePage;
  Page.preload = () =>
    load().then((module) => {
      loaded = module.default;
    });
  return Page;
}

export const SITE_PAGES: Readonly<Record<string, SitePage>> = {
  '/': sitePage(() => import('./Home')),
  '/models': sitePage(() => import('./Models')),
  '/get-started': sitePage(() => import('./GetStarted')),
  '/try': sitePage(() => import('./Try')),
  '/photo-to-art-shopify-app': sitePage(() => import('./PhotoToArt')),
  '/pet-portrait-app': sitePage(() => import('./PetPortraits')),
  '/guides/sell-custom-pet-portraits-on-shopify': sitePage(() => import('./PetPortraitGuide')),
  '/guides/best-ai-photo-to-art-apps-shopify': sitePage(() => import('./BestPhotoToArtApps')),
  '/guides/sell-christmas-pet-portraits-shopify': sitePage(() => import('./ChristmasPetPortraitGuide')),
  '/alternatives/customily': sitePage(() => import('./CustomilyAlternative')),
  '/privacy':sitePage(() => import('./Legal').then((m) => ({ default: m.PrivacyPage }))),
  '/terms': sitePage(() => import('./Legal').then((m) => ({ default: m.TermsPage }))),
  '/lp/photo-to-art-app': sitePage(() => import('./Landing').then((m) => ({ default: m.PhotoToArtLanding }))),
  '/lp/pet-portrait-app': sitePage(() => import('./Landing').then((m) => ({ default: m.PetPortraitLanding }))),
  '/lp/alternative': sitePage(() => import('./Landing').then((m) => ({ default: m.AlternativeLanding }))),
  '/lp/christmas-photo-gifts': sitePage(() => import('./Landing').then((m) => ({ default: m.ChristmasLanding }))),
  '/status': sitePage(() => import('./Status')),
  [HELP_INDEX_PATH]: sitePage(() => import('./Help')),
  ...Object.fromEntries(
    HELP_ARTICLES.map((a) => [helpPath(a.slug), sitePage(() => import('./Help').then((m) => ({ default: m.helpArticlePage(a.slug) })))]),
  ),
};

// Every registry path has a page, and only those.
if (SITE_PATHS.length !== Object.keys(SITE_PAGES).length || SITE_PATHS.some((path) => !SITE_PAGES[path])) {
  throw new Error('SITE_PAGES does not match the site route registry');
}

/** Loads the page component for `pathname` when it is a public page (no-op otherwise). */
export function preloadSitePage(pathname: string): Promise<void> {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return SITE_PAGES[path]?.preload() ?? Promise.resolve();
}

export function preloadAllSitePages(): Promise<void> {
  return Promise.all(Object.values(SITE_PAGES).map((page) => page.preload())).then(() => undefined);
}
